import { Utils } from '../../services';
import { screeningOpaqueFromPayload } from '../ScreeningOpaqueBadges';

export const PLAN_RESOURCE_SECTOR = 'sector';
export const PLAN_RESOURCE_PLACE = 'place';
export const PLAN_EVENT_SHIFT = 'shift';
export const PLAN_EVENT_SCREENING = 'screening';

export function planResourceId(kind, id) {
    if (id == null || id === '')
        return null;
    return `${kind}:${id}`;
}

export function parsePlanResourceId(resourceId) {
    if (resourceId == null || resourceId === '')
        return { kind: null, id: null };
    const value = String(resourceId);
    const separator = value.indexOf(':');
    if (separator <= 0)
        return { kind: PLAN_RESOURCE_SECTOR, id: value };
    return {
        kind: value.slice(0, separator),
        id: value.slice(separator + 1),
    };
}

export function planSectorIdFromResource(resourceId) {
    const parsed = parsePlanResourceId(resourceId);
    if (parsed.kind !== PLAN_RESOURCE_SECTOR || !parsed.id)
        return null;
    return parsed.id;
}

export function planSlotIsCreatable(resourceId) {
    return !!planSectorIdFromResource(resourceId);
}

function toDate(value) {
    if (!value)
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value.toDate === 'function') {
        const converted = value.toDate();
        return converted instanceof Date && !Number.isNaN(converted.getTime())
            ? converted
            : null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function localizedLabel(value, lang) {
    if (value == null || value === '')
        return '';
    if (typeof value === 'string' || typeof value === 'number')
        return String(value);
    const defaultLang = typeof Utils.default_lang === 'function'
        ? Utils.default_lang()
        : '';
    if (typeof Utils.resolveEventTranslatedTitle === 'function') {
        const resolved = Utils.resolveEventTranslatedTitle(value, lang, defaultLang);
        if (resolved)
            return resolved;
    }
    return Utils.localized_or_fallback(value, lang) || '';
}

function taskIsAssigned(task) {
    return !!(task?.user_id || task?.user?.id || task?.user?._id);
}

function screeningIdOf(task) {
    return task?.screening_id || task?.screening?.id || task?.screening?._id || null;
}

function placeIdOf(task) {
    return task?.screening?.place_id
        || task?.screening?.place?.id
        || task?.screening?.place?._id
        || null;
}

export function planOrphanTaskListParams(edition) {
    const params = { without_shift: 1 };
    const start = edition?.event_start_date || edition?.start_date;
    const stop = edition?.event_end_date || edition?.end_date;
    if (start)
        params.start_at_gte = start;
    if (stop)
        params.start_at_lte = stop;
    return params;
}

export function shiftCalendarEvents(shifts) {
    return (shifts || []).flatMap((shift) => {
        const start = toDate(shift?.start_at);
        const end = toDate(shift?.stop_at);
        const resourceId = planResourceId(PLAN_RESOURCE_SECTOR, shift?.sector_id);
        if (!start || !end || !resourceId)
            return [];
        const assigned = shift.nb_assigned ?? 0;
        const total = shift.nb_volunteers ?? 0;
        return [{
            id: shift.id,
            kind: PLAN_EVENT_SHIFT,
            start,
            end,
            resourceId,
            title: `${assigned} / ${total}`,
            shift,
        }];
    });
}

export function groupOrphanTasksByScreening(tasks, lang, occupancyById = null) {
    const grouped = new Map();

    (tasks || []).forEach((task) => {
        if (Utils.read_task_shift_id(task))
            return;
        const screeningId = screeningIdOf(task);
        const placeId = placeIdOf(task);
        if (!screeningId || !placeId)
            return;

        let entry = grouped.get(String(screeningId));
        if (!entry) {
            const screening = task.screening || {};
            const start = toDate(screening.start_at) || toDate(task.start_at);
            const end = toDate(screening.stop_at) || toDate(task.stop_at);
            if (!start || !end)
                return;
            const occupancy = occupancyById?.[String(screeningId)];
            entry = {
                screeningId: String(screeningId),
                placeId: String(placeId),
                placeName: screening.place?.name,
                title: screening.title,
                start,
                end,
                assigned: 0,
                total: 0,
                booked: occupancy
                    ? (Number(occupancy.booked) || 0)
                    : (Number(screening.booked) || 0),
                capacity: occupancy
                    ? (Number(occupancy.capacity) || 0)
                    : (Number(screening.capacity) || 0),
                posterUrl: screening.poster_url || screening.posterUrl || '',
                opaque: screeningOpaqueFromPayload(screening),
                tasks: [],
            };
            grouped.set(String(screeningId), entry);
        }

        entry.tasks.push(task);
        entry.total += 1;
        if (taskIsAssigned(task))
            entry.assigned += 1;
    });

    return [...grouped.values()].map((entry) => ({
        id: `screening:${entry.screeningId}`,
        kind: PLAN_EVENT_SCREENING,
        screeningId: entry.screeningId,
        start: entry.start,
        end: entry.end,
        resourceId: planResourceId(PLAN_RESOURCE_PLACE, entry.placeId),
        title: localizedLabel(entry.title, lang),
        assigned: entry.assigned,
        total: entry.total,
        booked: entry.booked,
        capacity: entry.capacity,
        posterUrl: entry.posterUrl,
        opaque: entry.opaque,
        tasks: entry.tasks,
        placeTitle: localizedLabel(entry.placeName, lang),
        placeId: entry.placeId,
    }));
}

/**
 * Screening ids whose events overlap a calendar viewport API window.
 *
 * @param {Array} screeningEvents
 * @param {{ stop_at_gte?: string, start_at_lte?: string }} rangeParams
 * @returns {string[]}
 */
export function planScreeningIdsInVisibleRange(screeningEvents, rangeParams) {
    const rangeStart = toDate(rangeParams?.stop_at_gte);
    const rangeEnd = toDate(rangeParams?.start_at_lte);
    const ids = [];
    const seen = new Set();
    (screeningEvents || []).forEach((event) => {
        const id = event?.screeningId;
        if (!id || seen.has(String(id)))
            return;
        const start = toDate(event.start);
        const end = toDate(event.end);
        if (!start || !end)
            return;
        if (rangeStart && end < rangeStart)
            return;
        if (rangeEnd && start > rangeEnd)
            return;
        seen.add(String(id));
        ids.push(String(id));
    });
    return ids;
}

export function placeResourcesFromScreeningEvents(screeningEvents, lang) {
    const byPlace = new Map();
    (screeningEvents || []).forEach((event) => {
        if (!event?.placeId)
            return;
        if (byPlace.has(event.placeId))
            return;
        byPlace.set(event.placeId, {
            id: planResourceId(PLAN_RESOURCE_PLACE, event.placeId),
            title: event.placeTitle || localizedLabel(event.title, lang),
        });
    });
    return [...byPlace.values()].sort((a, b) =>
        String(a.title).localeCompare(String(b.title))
    );
}
